/**
 * Dashboard Data Constants
 *
 * This file contains all static data for the Dashboard component.
 * In the future, this will be replaced by API calls to backend services.
 */

import type { MetricData, DashboardTab, FilterOption } from '../types/DashboardTypes';

// Selection bar options for main dashboard tabs
export const DASHBOARD_TABS: Array<{ id: DashboardTab; name: string }> = [
    { id: 'sales', name: 'Sales' },
    { id: 'bookings', name: 'Bookings' },
    { id: 'clients', name: 'Clients' },
    { id: 'staff', name: 'Staff' }
];

// Filter dropdown options
export const FILTER_OPTIONS = {
    year: [
        { id: 'ytd', name: 'Year to date' },
        { id: 'last-year', name: 'Last year' },
        { id: 'last-6-months', name: 'Last 6 months' },
        { id: 'last-3-months', name: 'Last 3 months' }
    ] as FilterOption[],

    comparison: [
        { id: 'jan-sep-2024', name: 'Jan 1, 2024-Sep 12, 2024' },
        { id: 'jan-sep-2023', name: 'Jan 1, 2023-Sep 12, 2023' },
        { id: 'custom', name: 'Custom range' }
    ] as FilterOption[],

    businessType: [
        { id: 'all', name: 'All business types' },
        { id: 'veterinary', name: 'Veterinary' },
        { id: 'grooming', name: 'Grooming' },
        { id: 'boarding', name: 'Boarding' },
        { id: 'daycare', name: 'Daycare' }
    ] as FilterOption[],

    region: [
        { id: 'all', name: 'All regions' },
        { id: 'north-america', name: 'North America' },
        { id: 'europe', name: 'Europe' },
        { id: 'asia', name: 'Asia' },
        { id: 'latin-america', name: 'Latin America' }
    ] as FilterOption[],

    location: [
        { id: 'all', name: 'All locations' },
        { id: 'new-york', name: 'New York' },
        { id: 'california', name: 'California' },
        { id: 'texas', name: 'Texas' },
        { id: 'florida', name: 'Florida' }
    ] as FilterOption[]
};

// Mock metrics data by tab
// TODO: Replace with API calls in future implementation
export const DASHBOARD_METRICS: Record<DashboardTab, MetricData[]> = {
    sales: [
        {
            title: 'Total sales',
            value: '$329,363',
            change: '+35%',
            avgPrice: '$275.89'
        },
        {
            title: 'Grooming services',
            value: '$75,120',
            change: '-35%',
            avgPrice: '$95.06'
        },
        {
            title: 'Daycare services',
            value: '$95,375',
            change: '+35%',
            avgPrice: '$50.77'
        },
        {
            title: 'Boarding services',
            value: '$100,000',
            change: '+35%',
            avgPrice: '$100.00'
        },
        {
            title: 'Add-ons',
            value: '$25,790',
            change: '-7%',
            avgPrice: '$15.10'
        },
        {
            title: 'Products',
            value: '$5,250',
            change: '-5%',
            avgPrice: '$22.50'
        },
        {
            title: 'Custom items',
            value: '$2,150',
            change: '+2,000%',
            avgPrice: '$2,150.00'
        }
    ],

    bookings: [
        {
            title: 'Total bookings',
            value: '1,789',
            change: '+35%',
            perDay: '7.6 bookings per day'
        },
        {
            title: 'Booking lead time',
            value: '2 days',
            change: '+22%',
            perDay: 'Before an appointment'
        },
        {
            title: 'Clients served',
            value: '251',
            change: '-4%',
            perDay: '3 clients per day'
        },
        {
            title: 'Pets served',
            value: '376',
            change: '+8%',
            perDay: '5 pets per day'
        }
    ],

    clients: [
        {
            title: 'Active clients',
            value: '1,913',
            change: '+35%'
        },
        {
            title: 'New clients',
            value: '139',
            change: '-35%'
        },
        {
            title: 'Membership/Package clients',
            value: '1,011',
            change: '+35%'
        },
        {
            title: 'Client retention rate',
            value: '65%',
            change: '-7%'
        },
        {
            title: 'Churn rate',
            value: '14%',
            change: '-5%'
        },
        {
            title: 'Lifetime value (LTV)',
            value: '$2,150',
            change: '+15%'
        },
        {
            title: 'Visit frequency (days)',
            value: '31',
            change: '+9%'
        }
    ],

    staff: [
        {
            title: 'Staff utilization',
            value: '1,913',
            change: '+35%'
        },
        {
            title: 'Revenue per staff',
            value: '$233',
            change: '-35%'
        },
        {
            title: 'Tips per staff',
            value: '$15',
            change: '+35%'
        },
        {
            title: 'Client retention rate',
            value: '65%',
            change: '-7%'
        },
        {
            title: 'Staff retention',
            value: '14%',
            change: '-5%'
        }
    ]
};

// Default filter values
export const DEFAULT_FILTERS = {
    year: 'ytd',
    comparison: 'jan-sep-2024',
    businessType: 'all',
    region: 'all',
    location: 'all'
};
